import { Box, Button, Paper, Typography, Zoom } from '@mui/material';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import AnalysisHeader from '../components/analyses/AnalysisHeader';
import AnalysisParameters from '../components/analyses/AnalysisParameters';
import ScoreMetrics from '../components/common/ScoreMetrics';
import Status from '../components/common/Status';
import TotalScoreConsumption from '../components/common/TotalScoreConsumption';
import ContainersAccordion from '../components/containers/ContainersAccordion';
import Explanation from '../components/demo/Explanation';
import StickyInAbsoluteContainer from '../components/demo/StickyInAbsoluteContainer';
import MainLayout from '../layouts/MainLayout';
import {
    analysis,
    analysisReference,
    containers,
    containersReference,
    scenario,
    scenarioReference,
} from '../services/demoData';

const demo = () => {
    const letsClickRef = useRef();
    const conclusionRef = useRef();
    const bottomReachedRef = useRef();
    const [showTryModal, setShowTryModal] = useState(false);
    useEffect(() => {
        if (letsClickRef?.current && bottomReachedRef?.current) {
            const clickOnAccordion = (valueToSet) => {
                const accordion = document.querySelector(
                    '[aria-controls="container_1-content"]'
                );
                if (accordion.getAttribute('aria-expanded') !== String(valueToSet)) {
                    accordion.click();
                }
            };

            const options = {
                rootMargin: '10% 0px -70% 0px',
                threshold: 1.0,
            };

            const expandAccordionObserver = new IntersectionObserver((e) => {
                if (e[0].isIntersecting) {
                    clickOnAccordion(true);
                }
            }, options);

            const bottomReachedObserver = new IntersectionObserver((e) => {
                setShowTryModal(e[0].isIntersecting);
                if (e[0].isIntersecting) {
                    clickOnAccordion(false);
                }
            });

            expandAccordionObserver.observe(letsClickRef.current);
            expandAccordionObserver.observe(conclusionRef.current);
            bottomReachedObserver.observe(bottomReachedRef.current);

            return () => {
                expandAccordionObserver?.disconnect();
                bottomReachedObserver?.disconnect();
            };
        }
    }, [letsClickRef, bottomReachedRef]);
    return (
        <MainLayout loader={false}>
            <>
                <AnalysisHeader
                    analysis={analysis}
                    analysisReference={analysisReference}
                />

                <Box sx={{ maxWidth: 900, m: 'auto' }}>
                    <Box
                        sx={{
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'baseline', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            '&>*': {
                                my: 2,
                            },
                        }}
                    >
                        <Box>
                            {analysis.score?.wh && (
                                <>
                                    <Typography>Global estimated consumption:</Typography>
                                    <Box
                                        id="totalScore"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <TotalScoreConsumption
                                            score={analysis.score}
                                            scoreReference={analysisReference?.score}
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>

                        <AnalysisParameters
                            analysis={analysis}
                            showDeleteButton={true}
                            expanded
                        />
                    </Box>
                    <Zoom in={true} style={{ transitionDelay: '700ms' }}>
                        <Box>
                            <Explanation
                                arrowLeft
                                arrowLeftTransform="rotate(-90deg) scaleY(-1)"
                            >
                                <Box>
                                    <Typography variant="h5">
                                        Uh-oh, the estimated carbon footprint of the Acme
                                        e-shop just increased by 11%.
                                    </Typography>
                                    <Typography variant="subtitle1">
                                        Scroll down to see the breakdown by system metric.
                                    </Typography>
                                </Box>
                            </Explanation>
                        </Box>
                    </Zoom>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: 12,
                        }}
                    >
                        <Box>
                            <Typography variant="h5">{scenario.name}</Typography>
                        </Box>
                        <Status status={scenario.status} />
                    </Box>
                    <ScoreMetrics
                        score={scenario.score}
                        precision={scenario.precision}
                        scoreReference={scenarioReference?.score}
                        displayTotal
                    />
                    <Zoom in={true} style={{ transitionDelay: '1000ms' }}>
                        <Box sx={{ mt: 2, mb: 6 }}>
                            <Explanation
                                arrowLeft
                                arrowLeftTransform="rotate(90deg) scaleX(-1)"
                                arrowRight
                                arrowRightTransform="rotate(90deg) scaleY(-1) scaleX(-1)"
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h5">
                                        The network emissions grew much more than the
                                        other metrics.
                                    </Typography>
                                    <Typography variant="subtitle1">
                                        Let&apos;s dig further to understand the root
                                        cause.
                                    </Typography>
                                </Box>
                            </Explanation>
                        </Box>
                    </Zoom>

                    <Box sx={{ minHeight: '2400px', position: 'relative' }}>
                        <Box
                            sx={{
                                position: 'sticky',
                                top: '180px',
                                width: '100%',
                                zIndex: 2,
                                backgroundColor: 'grey.light',
                                height: 'calc(100vh - 160px)',
                            }}
                        >
                            <ContainersAccordion
                                containers={containers}
                                containersReference={containersReference}
                                milestones={scenario.milestones}
                                disabled
                            />
                        </Box>
                        <StickyInAbsoluteContainer itemNumber={0}>
                            <Explanation arrowLeft ref={letsClickRef}>
                                <Box>
                                    <Typography variant="h5">
                                        The browser container contributes a lot to the
                                        network footprint.
                                    </Typography>
                                    <Typography variant="subtitle1">
                                        The user scenario contains several actions, which
                                        one is the culprit?
                                    </Typography>
                                </Box>
                            </Explanation>
                        </StickyInAbsoluteContainer>
                        <StickyInAbsoluteContainer itemNumber={1}>
                            <Explanation
                                arrowRight
                                arrowRightTransform="rotate(90deg) scaleY(-1)"
                            >
                                <Box>
                                    <Typography variant="h5">
                                        The network chart shows a high peak at around 3s.
                                    </Typography>
                                    <Typography variant="subtitle1">
                                        Hover over the first vertical line at 2s: This is
                                        the &quot;Get list results&quot; step.
                                    </Typography>
                                </Box>
                            </Explanation>
                        </StickyInAbsoluteContainer>
                        <StickyInAbsoluteContainer itemNumber={2}>
                            <Explanation ref={conclusionRef}>
                                <Box>
                                    <Typography variant="h5">
                                        That&apos;s the reason: the developers just pushed
                                        a change in the product list pagination.
                                    </Typography>
                                    <Typography variant="subtitle1">
                                        This new feature has a high carbon impact.
                                        Designers and developers should look for an
                                        alternative solution.
                                    </Typography>
                                </Box>
                            </Explanation>
                        </StickyInAbsoluteContainer>
                    </Box>
                    <Box ref={bottomReachedRef} sx={{ height: 1 }}></Box>
                </Box>
                {showTryModal && (
                    <Box
                        sx={{
                            position: 'fixed',
                            width: '100%',
                            height: '100%',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 3,
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Zoom in={showTryModal}>
                            <Paper
                                elevation={3}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    maxWidth: 600,
                                    p: 4,
                                    mx: 'auto',
                                    backgroundColor: 'white.light',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="h2" gutterBottom>
                                    Add the carbon footprint to your quality signals
                                </Typography>
                                <Image
                                    src="/comparator.svg"
                                    width="200"
                                    height="150"
                                    alt="Man thinking beween trees"
                                />
                                <Button
                                    href="/register"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                >
                                    Try GreenFrame Now
                                </Button>
                                <Typography variant="subtitle1">1 month free</Typography>
                            </Paper>
                        </Zoom>
                    </Box>
                )}
            </>
        </MainLayout>
    );
};

export default demo;
